// src/components/admin/NewsList.js
import React, { useState, useEffect, useRef } from 'react';
import { newsAPI, imagaeAPI } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Modal, Button } from 'react-bootstrap';
import { STATE, DISTRICTS, CATEGORIES } from './constants';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const NewsList = () => {
    const [loading, setLoading] = useState(false);
    const [newsItems, setNewsItems] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const editPhotoRef = useRef(null);

    // Search & Pagination state
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Fetch all news
    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await newsAPI.getAll();
            setNewsItems(res.data.data);
        } catch (error) {
            console.error('Error fetching news:', error);
            toast.error('Failed to load news.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, []);

    // Reset page when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // ----- Filter & Paginate -----
    const filteredItems = newsItems.filter(item =>
        item.news_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.categories?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.district?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.status?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const paginatedItems = filteredItems.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // ----- Edit handlers -----
    const handleEditClick = (item) => {
        setSelectedItem({
            ...item,
            breaking_news: Boolean(item.breaking_news),
        });
        setShowEditModal(true);
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        setSelectedItem((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value,
        }));
    };

    const handleEditQuillChange = (value) => {
        setSelectedItem((prev) => ({
            ...prev,
            news_content: value,
        }));
    };

    const handleUpdateItem = async () => {
        setLoading(true);
        try {
            const data = new FormData();
            data.append('news_title', selectedItem.news_title || '');
            data.append('news_content', selectedItem.news_content || '');
            data.append('categories', selectedItem.categories || CATEGORIES[0]);
            data.append('state', STATE);
            data.append('district', selectedItem.district || DISTRICTS[0]);
            data.append('video_url', selectedItem.video_url || '');
            data.append('status', selectedItem.status || 'active');
            data.append('breaking_news', selectedItem.breaking_news ? '1' : '0');

            if (selectedItem.photo instanceof File) {
                data.append('photo', selectedItem.photo);
            }

            await newsAPI.update(selectedItem.id, data);
            toast.success('News updated successfully!');
            setShowEditModal(false);
            fetchItems();
        } catch (error) {
            console.error(error);
            const errors = error.response?.data?.errors;
            const msg = errors ? Object.values(errors).flat().join(' ') : 'Error updating news.';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    // ----- Delete handlers -----
    const confirmDelete = (id) => {
        toast.info(
            <div>
                <p className="mb-2">Are you sure you want to delete this news?</p>
                <div className="d-flex justify-content-end">
                    <button
                        className="btn btn-sm btn-danger mr-2"
                        onClick={() => {
                            toast.dismiss();
                            handleDeleteItem(id);
                        }}
                    >
                        Yes
                    </button>
                    <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => toast.dismiss()}
                    >
                        No
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeOnClick: false,
                closeButton: false,
                icon: false,
            }
        );
    };

    const handleDeleteItem = async (id) => {
        setLoading(true);
        try {
            await newsAPI.delete(id);
            toast.success('News deleted successfully!');
            fetchItems();
        } catch (error) {
            console.error(error);
            toast.error('Error deleting news.');
        } finally {
            setLoading(false);
        }
    };

    // ----- Helper to format photo URL -----
    const getPhotoUrl = (photoPath) => {
        if (!photoPath) return null;
        if (photoPath.startsWith('http')) return photoPath;
        return `${imagaeAPI}/storage/${photoPath}`;
    };

    return (
        <>
            <div className="main-content">
                <div className="content-wrapper">
                    {/* List Card */}
                    <div className="list-card">
                        <div className="list-card-header d-flex justify-content-between align-items-center">
                            <h5><i className="fas fa-newspaper me-2"></i>All News</h5>
                            <div style={{ width: '250px' }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by title..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="list-card-body">
                            {loading && <p>Loading...</p>}
                            {!loading && filteredItems.length === 0 && <p>No news found.</p>}
                            {!loading && filteredItems.length > 0 && (
                                <>
                                    <div className="table-responsive">
                                        <table className="display compact table table-striped table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Title</th>
                                                    <th>Category</th>
                                                    <th>District</th>
                                                    <th>Photo</th>
                                                    <th>Video</th>
                                                    <th>Breaking</th>
                                                    <th>Status</th>
                                                    <th>Edit</th>
                                                    <th>Delete</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedItems.map((item, index) => (
                                                    <tr key={item.id}>
                                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                        <td>{item.news_title}</td>
                                                        <td>{item.categories}</td>
                                                        <td>{item.district}</td>
                                                        <td>
                                                            {item.photo ? (
                                                                <img
                                                                    src={`${imagaeAPI}/uploads/news/${item.photo}`}
                                                                    alt="Product Image"
                                                                    width="50"
                                                                    height="50"
                                                                    style={{ objectFit: "cover", cursor: "pointer" }}
                                                                    onClick={() => window.open(`${imagaeAPI}/uploads/news/${item.photo}`, "_blank")}
                                                                />
                                                            ) : "-"}
                                                        </td>
                                                        <td>
                                                            {item.video_url ? (
                                                                <a href={item.video_url} target="_blank" rel="noopener noreferrer">
                                                                    <i className="fas fa-video"></i>
                                                                </a>
                                                            ) : '-'}
                                                        </td>
                                                        <td>{item.breaking_news ? 'Yes' : 'No'}</td>
                                                        <td>{item.status}</td>
                                                        <td>
                                                            <a
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    handleEditClick(item);
                                                                }}
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </a>
                                                        </td>
                                                        <td>
                                                            <a
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    confirmDelete(item.id);
                                                                }}
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </a>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <nav className="mt-3">
                                            <ul className="pagination justify-content-center">
                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                        disabled={currentPage === 1}
                                                    >
                                                        Previous
                                                    </button>
                                                </li>
                                                {[...Array(totalPages).keys()].map(num => (
                                                    <li key={num + 1} className={`page-item ${currentPage === num + 1 ? 'active' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => setCurrentPage(num + 1)}
                                                        >
                                                            {num + 1}
                                                        </button>
                                                    </li>
                                                ))}
                                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        Next
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <ToastContainer />
                </div>
            </div>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit News</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedItem && (
                        <form>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label>Title</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="news_title"
                                        value={selectedItem.news_title || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label>Category</label>
                                    <select
                                        className="form-control"
                                        name="categories"
                                        value={selectedItem.categories || ''}
                                        onChange={handleEditChange}
                                    >
                                        <option value="">Select</option>
                                        {CATEGORIES.map((cat) => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="row g-3 mt-2">
                                <div className="col-md-6">
                                    <label>State</label>
                                    <input type="text" className="form-control" value={STATE} disabled />
                                </div>
                                <div className="col-md-6">
                                    <label>District</label>
                                    <select
                                        className="form-control"
                                        name="district"
                                        value={selectedItem.district || ''}
                                        onChange={handleEditChange}
                                    >
                                        <option value="">Select</option>
                                        {DISTRICTS.map((dist) => (
                                            <option key={dist} value={dist}>{dist}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="row g-3 mt-2">
                                <div className="col-md-12">
                                    <label>Content</label>
                                    <ReactQuill
                                        theme="snow"
                                        value={selectedItem.news_content || ''}
                                        onChange={handleEditQuillChange}
                                        style={{ height: '150px', marginBottom: '50px' }}
                                    />
                                </div>
                            </div>

                            <div className="row g-3 mt-2">
                                <div className="col-md-6">
                                    <label>Photo</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        name="photo"
                                        onChange={handleEditChange}
                                        ref={editPhotoRef}
                                    />
                                    {selectedItem.photo && typeof selectedItem.photo === 'string' && (
                                        <div className="mt-2">
                                            <img src={`${imagaeAPI}/uploads/news/${selectedItem.photo}`} alt="Current" width="80" />
                                            <p className="text-muted small">Current image</p>
                                        </div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label>Video URL</label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        name="video_url"
                                        value={selectedItem.video_url || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>
                            </div>

                            <div className="row g-3 mt-2">
                                <div className="col-md-4">
                                    <label>Status</label>
                                    <select
                                        className="form-control"
                                        name="status"
                                        value={selectedItem.status || 'active'}
                                        onChange={handleEditChange}
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                                <div className="col-md-4 d-flex align-items-center">
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            name="breaking_news"
                                            checked={selectedItem.breaking_news || false}
                                            onChange={handleEditChange}
                                        />
                                        <label className="form-check-label">Breaking News</label>
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleUpdateItem} disabled={loading}>
                        {loading ? 'Updating...' : 'Update'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default NewsList;